import React, { useMemo } from 'react';
import s from './ProgressBar.scss';

export interface getProgressCoordsSettings
  extends React.SVGProps<SVGSVGElement> {
  xStart?: number;
  yStart?: number;
  width?: number;
  height?: number;
  upperLeftCorner?: number;
  upperRightCorner?: number;
  lowerRightCorner?: number;
  lowerLeftCorner?: number;
}

interface ColorStop {
  id: string | number;
  color: string;
  offset: string | number;
}

export interface MeterFillSettings extends React.SVGProps<SVGSVGElement> {
  id: string;
  color?: string;
  colors?: ColorStop[];
}
export interface MeterSettings extends React.SVGProps<SVGSVGElement> {
  backgroundColor: string;
  clipPathId: string;
  fillId: string;
  percentCompleted: number;
  height: number;
  width: number;
}

export interface ProgressBarShapeSettings
  extends React.SVGProps<SVGSVGElement> {
  arrowSettings: {
    arrowWidth: number;
    stemHeight: number;
  };
  cornerProperties: {
    upperLeftCorner: number;
    upperRightCorner: number;
    lowerRightCorner: number;
    lowerLeftCorner: number;
  };
  clipPathID: string;
  height: number;
  width: number;
}

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  completed: number;
  goal: number;
  height: number;
  width: number;
  percentInContainer?: number;
  arrowWidth?: number;
  stemHeight?: number;
  upperLeftCorner?: number;
  upperRightCorner?: number;
  lowerRightCorner?: number;
  lowerLeftCorner?: number;
  backgroundColor?: string;
  meterColor?: string;
  meterColors?: Array<{
    color: string;
    offset: number;
    id: number;
  }>;
}

export function getProgressCoords(settings: getProgressCoordsSettings): string {
  const {
    xStart: x = 0,
    yStart: y = 0,
    width: w = 0,
    height: h = 0,
    upperLeftCorner: r1 = 0,
    upperRightCorner: r2 = 0,
    lowerRightCorner: r3 = 0,
    lowerLeftCorner: r4 = 0,
  } = settings;

  const start = `M ${x + r1}, ${y}`;
  const top = `L ${x + w - r2}, ${y}`;
  const upRtC = `Q ${x + w}, ${y} ${x + w}, ${y + r2}`;
  const right = `L ${x + w}, ${y + h - r3}`;
  const loRtC = `Q ${x + w}, ${y + h} ${x + w - r3}, ${y + h}`;
  const bottom = `L ${x + r4}, ${y + h}`;
  const loLtC = `Q ${x}, ${y + h} ${x}, ${y + h - r4}`;
  const left = `L ${x}, ${y + r1}`;
  const upLfC = `Q ${x}, ${y} ${x + r1}, ${y}`;
  const end = 'Z';

  return `${start} ${top} ${upRtC} ${right} ${loRtC} ${bottom} ${loLtC} ${left} ${upLfC} ${end}`;
}

export function MeterFill(settings: MeterFillSettings): JSX.Element {
  const { id, color, colors } = settings;
  const fillColor = colors || [{ color: color, offset: 0, id: 0 }];

  return (
    <svg>
      <defs>
        <linearGradient id={id}>
          {fillColor.map((stop) => (
            <stop key={stop.id} stopColor={stop.color} offset={stop.offset} />
          ))}
        </linearGradient>
      </defs>
    </svg>
  );
}

export function Meter(settings: MeterSettings): JSX.Element {
  const {
    backgroundColor,
    clipPathId,
    fillId,
    percentCompleted,
    height,
    width,
  } = settings;
  const completedWidth =
    percentCompleted > 100 ? width : (percentCompleted * width) / 100;

  return (
    <svg width={width} height={height}>
      <defs>
        <clipPath id={clipPathId}>
          <rect width={completedWidth} height={height} />
        </clipPath>
        <linearGradient id={fillId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset={`${percentCompleted}%`} stopColor="green" />
          <stop offset={`${percentCompleted}%`} stopColor="transparent" />
        </linearGradient>
      </defs>
      <rect width={width} height={height} fill={backgroundColor} />
      <rect width={completedWidth} height={height} fill={`url(#${fillId})`} />
    </svg>
  );
}

export function ProgressBarShape(settings: ProgressBarShapeSettings) {
  const {
    arrowSettings: { arrowWidth, stemHeight },
    cornerProperties,
    clipPathID,
    height,
    width,
  } = settings;

  const stemCoordinates = getProgressCoords({
    xStart: 0,
    yStart: stemHeight ? (height - stemHeight) / 2 : 0,
    width: arrowWidth ? width - arrowWidth : width,
    height: stemHeight || height,
    ...cornerProperties,
  });

  return (
    <svg width={width} height={height}>
      <defs>
        <clipPath id={clipPathID}>
          <path d={stemCoordinates} />
          {arrowWidth && (
            <polygon
              points={`${width - arrowWidth},0 ${
                width - arrowWidth
              },${height} ${width},${height / 2}`}
            />
          )}
        </clipPath>
      </defs>
    </svg>
  );
}

export function getRandomDefsID(prefix: string) {
  return `${prefix}__${Math.floor(Math.random() * 1000)}`;
}

export default function ProgressBar(props: ProgressBarProps) {
  const {
    arrowWidth = 0,
    backgroundColor = '#EAEAEA',
    completed,
    className,
    height,
    goal,
    lowerRightCorner = 0,
    lowerLeftCorner = 0,
    meterColor = '#00A699',
    meterColors = [{ color: meterColor, offset: 0, id: 0 }],
    width,
    percentInContainer = 100,
    stemHeight = 0,
    upperLeftCorner = 0,
    upperRightCorner = 0,
    children,
    ...rest
  } = props;
  const calcComponentStyles = useMemo<React.CSSProperties>(
    () => ({
      paddingBottom: `${(height / width) * percentInContainer}%`,
      width: `${percentInContainer}%`,
    }),
    [height, width, percentInContainer]
  );
  const clipPathID = getRandomDefsID('clipPath');
  const fillID = getRandomDefsID('meterGadient');

  const arrowSettings = useMemo<ProgressBarShapeSettings['arrowSettings']>(
    () => ({ arrowWidth, stemHeight }),
    [arrowWidth, stemHeight]
  );

  return (
    <figure
      className={`${s.progress} ${className}`}
      {...rest}
      style={calcComponentStyles}
    >
      <svg
        className={s.progressSVG}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="progressbar"
        aria-valuenow={completed}
        aria-valuemin={0}
        aria-valuemax={goal}
      >
        <defs>
          <ProgressBarShape
            arrowSettings={arrowSettings}
            cornerProperties={{
              upperLeftCorner,
              upperRightCorner,
              lowerRightCorner,
              lowerLeftCorner,
            }}
            height={height}
            clipPathID={clipPathID}
            width={width}
          />
          <MeterFill id={fillID} color={meterColor} colors={meterColors} />
        </defs>
        <Meter
          backgroundColor={backgroundColor}
          height={height}
          width={width}
          clipPathId={clipPathID}
          fillId={fillID}
          percentCompleted={(completed / goal) * 100}
        />
      </svg>
      {children}
    </figure>
  );
}

ProgressBar.displayName = 'ProgressBar';
